import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { isWithheldNumber } from './displayPhone'
import { SCENE_ROUTE, isSceneRoute } from './sceneRoute'
import { useCallState, useTelephony } from './TelephonyProvider'

/**
 * Présentation des appels ENTRANTS — rendue par le provider, donc visible sur
 * TOUTE page (c'est l'exigence du cahier : « entrants sur toute page »).
 *
 * Sans elle, le Device global présentait bien l'appel à la machine à états,
 * mais seule la page Softphone savait l'afficher : un agent en train de
 * consulter ses contacts voyait son badge passer à « Appel entrant » sans
 * aucun moyen de décrocher — l'appel sonnait 25 s puis finissait en manqué.
 *
 * Vue PURE sur la machine, comme le badge : aucun état local, aucun appel
 * réseau. Elle ne s'affiche que pendant la phase `incoming`.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * DÉCROCHER HORS DE LA SCÈNE MÈNE À LA SCÈNE (issue #220, constat prod du
 * 2026-10-12)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'overlay disparaît au décroché (phase `active`) — et sur toute page autre
 * que `/v2/softphone`, RIEN ne prenait le relais : appel actif, audio OK,
 * aucun « Raccrocher », aucune qualification au raccroché, et l'agent qui
 * rafraîchissait restait compté « en wrapup » 180 s côté serveur (entrants
 * suivants mis en file puis MISSED). La scène lit la machine globale : y
 * naviguer juste après l'acceptation suffit, elle affiche l'appel `active`
 * puis le wrapup. Déjà sur la scène, on ne navigue pas vers soi-même.
 *
 * Le routeur est disponible ici : le provider est monté SOUS le
 * `BrowserRouter` de `main.tsx` (la pastille, rendue par le même arbre,
 * utilise déjà `useNavigate`).
 */
export function IncomingCallOverlay() {
  const { t } = useTranslation('telephony')
  const call = useCallState()
  const telephony = useTelephony()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  if (call.phase !== 'incoming') return null

  const accept = () => {
    // L'ordre est le contrat : d'abord décrocher (le leg est accepté quoi
    // qu'il arrive à la navigation), ensuite seulement changer d'écran.
    telephony.acceptIncoming()
    if (!isSceneRoute(pathname)) navigate(SCENE_ROUTE)
  }

  // « NUMÉRO MASQUÉ » (B3) : un appelant anonyme arrive avec un marqueur
  // Twilio (`anonymous`, `+266696687`…) en guise de numéro — l'agent lit
  // « Numéro masqué », pas du jargon SIP.
  const withheld = isWithheldNumber(call.phoneNumber)

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('incoming.title')}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm rounded-3xl border border-slate-700/60 bg-slate-900 p-8 text-center shadow-2xl">
        <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-blue-400">
          {t('incoming.title')}
        </p>
        <p className="mb-1 text-3xl font-bold text-white">
          {call.contactName ||
            (withheld ? t('call.withheldNumber') : call.phoneNumber) ||
            t('call.unknownNumber')}
        </p>
        {call.contactName && call.phoneNumber && !withheld && (
          <p className="mb-6 text-sm text-slate-400">{call.phoneNumber}</p>
        )}

        <div className="mt-6 flex gap-3">
          {/* Le bouton de refus appelle `reject()` côté SDK : Twilio reprend la
              main immédiatement et l'appel continue de sonner chez les autres
              agents, au lieu d'attendre l'expiration de la sonnerie. */}
          <button
            onClick={() => telephony.rejectIncoming()}
            className="flex-1 rounded-2xl bg-red-600 py-4 font-semibold text-white hover:bg-red-700"
          >
            {t('incoming.reject')}
          </button>
          <button
            onClick={accept}
            className="flex-1 rounded-2xl bg-green-600 py-4 font-semibold text-white hover:bg-green-700"
          >
            {t('incoming.accept')}
          </button>
        </div>
      </div>
    </div>
  )
}
